// Отчёт звонка: `GET /api/calls/{id}` отдаёт `report` сырым JSON события
// call.result (callv1.CallReport). Это СВЁРТКА — факты звонка и агрегаты по
// ходам; она едет терминальным событием, поэтому список звонков и биллинг
// работают, даже если разбор не доехал. Ходы, реплики абонента и события
// исполнения живут своим трактом — `model/telemetry.ts`.

import { bool, isRecord, list, num, rec, stamp, str } from './parse'

export interface TranscriptSegment {
  role: 'bot' | 'user'
  text: string
  // мс от начала звонка; 0/0 — движок оси не проставил
  tStartMs: number
  tEndMs: number
  turnIdx: number
}

export interface GreetingRecord {
  turnIdx: number
  triggerOnsetAt: string
  releasedAt: string
  protectMs: number
  swallowedCommits: number
}

export interface RecordingRecord {
  blobKey: string
  bytes: number
  durMs: number
  truncated: boolean
}

export interface MediaStats {
  rxAudioMs: number
  rxDropped: number
  sttUplinkDropped: number
  txFrames: number
  txUnderruns: number
  txMissed: number
  txLate: number
  txWriteErrs: number
}

// ok === null — отчёт без статуса (движок старше контракта): локальной
// телеметрии верить можно, но подтверждения нет.
export interface PersistStatus {
  ok: boolean | null
  err: string
  failedTables: string[]
}

// Стадия меряется нижней медианой и худшим ходом: телефонная латентность
// хвостатая, среднее спрятало бы как раз те ходы, которые абонент заметил.
export interface StageStats {
  turns: number
  p50Ms: number
  maxMs: number
}

export interface CallStats {
  turns: number
  normal: number
  interrupted: number
  eouWait: StageStats
  settle: StageStats
  llmTtft: StageStats
  ttsTtfa: StageStats
  fao: StageStats
  perceived: StageStats
  greetingFaoMs: number
  settleHard: number
  emptyCommits: number
  prematureEou: number
  backchannels: number
  barges: number
  falseBarges: number
  specStarted: number
  specPromoted: number
  specLeadAvgMs: number
  promptTokens: number
  cachedTokens: number
  completionTokens: number
  llmLengthFinish: number
  decideErrs: number
  pacerUnderruns: number
  ttsErrors: number
  unheardFrames: number
}

export interface CallReport {
  callId: string
  language: string
  startedAt: string
  answeredAt: string
  endedAt: string
  llmTarget: string
  transcript: TranscriptSegment[]
  greeting: GreetingRecord | null
  recording: RecordingRecord | null
  media: MediaStats | null
  persist: PersistStatus
  stats: CallStats | null
  intentHint: string
  recordingKey: string
  raw: unknown
}

function parseStage(v: unknown): StageStats {
  const s = rec(v)
  return { turns: num(s.turns), p50Ms: num(s.p50_ms), maxMs: num(s.max_ms) }
}

// Сводка есть только у отчётов с телеметрией: пустой объект (omitzero на
// стороне движка) — это «агрегатов нет», а не «всё по нулям».
function parseStats(v: unknown): CallStats | null {
  if (!isRecord(v) || Object.keys(v).length === 0) return null
  return {
    turns: num(v.turns),
    normal: num(v.normal),
    interrupted: num(v.interrupted),
    eouWait: parseStage(v.eou_wait),
    settle: parseStage(v.settle),
    llmTtft: parseStage(v.llm_ttft),
    ttsTtfa: parseStage(v.tts_ttfa),
    fao: parseStage(v.fao),
    perceived: parseStage(v.perceived),
    greetingFaoMs: num(v.greeting_fao_ms),
    settleHard: num(v.settle_hard),
    emptyCommits: num(v.empty_commits),
    prematureEou: num(v.premature_eou),
    backchannels: num(v.backchannels),
    barges: num(v.barges),
    falseBarges: num(v.false_barges),
    specStarted: num(v.spec_started),
    specPromoted: num(v.spec_promoted),
    specLeadAvgMs: num(v.spec_lead_avg_ms),
    promptTokens: num(v.prompt_tokens),
    cachedTokens: num(v.cached_tokens),
    completionTokens: num(v.completion_tokens),
    llmLengthFinish: num(v.llm_length_finish),
    decideErrs: num(v.decide_errs),
    pacerUnderruns: num(v.pacer_underruns),
    ttsErrors: num(v.tts_errors),
    unheardFrames: num(v.unheard_frames),
  }
}

function parseTranscript(v: unknown): TranscriptSegment[] {
  const out: TranscriptSegment[] = []
  for (const item of list(v)) {
    const segment = rec(item)
    const text = str(segment.text)
    if (!text) continue
    out.push({
      role: segment.role === 'bot' ? 'bot' : 'user',
      text,
      tStartMs: num(segment.t_start_ms),
      tEndMs: num(segment.t_end_ms),
      turnIdx: num(segment.turn_idx),
    })
  }
  return out
}

export function parseGreeting(v: unknown): GreetingRecord | null {
  if (!isRecord(v)) return null
  return {
    turnIdx: num(v.turn_idx),
    triggerOnsetAt: stamp(v.trigger_onset_at),
    releasedAt: stamp(v.released_at),
    protectMs: num(v.protect_ms),
    swallowedCommits: num(v.swallowed_commits),
  }
}

export function parseRecording(v: unknown): RecordingRecord | null {
  if (!isRecord(v)) return null
  return {
    blobKey: str(v.blob_key),
    bytes: num(v.bytes),
    durMs: num(v.dur_ms),
    truncated: bool(v.truncated),
  }
}

export function parsePersist(v: unknown): PersistStatus {
  if (!isRecord(v)) return { ok: null, err: '', failedTables: [] }
  return {
    ok: typeof v.ok === 'boolean' ? v.ok : null,
    err: str(v.err),
    failedTables: list(v.failed_tables).map(str).filter(Boolean),
  }
}

export function parseMedia(v: unknown): MediaStats | null {
  if (!isRecord(v) || Object.keys(v).length === 0) return null
  return {
    rxAudioMs: num(v.rx_audio_ms),
    rxDropped: num(v.rx_dropped),
    sttUplinkDropped: num(v.stt_uplink_dropped),
    txFrames: num(v.tx_frames),
    txUnderruns: num(v.tx_underruns),
    txMissed: num(v.tx_missed),
    txLate: num(v.tx_late),
    txWriteErrs: num(v.tx_write_errs),
  }
}

export function parseCallReport(report: unknown): CallReport {
  const root = rec(report)
  const result = rec(root.result)
  const artifact = list(result.artifacts)
    .map(rec)
    .find((a) => str(a.kind) === 'recording' && str(a.blob_key) !== '')
  const recording = parseRecording(root.recording)

  return {
    callId: str(root.call_id),
    language: str(root.language),
    startedAt: stamp(root.started_at),
    answeredAt: stamp(root.answered_at),
    endedAt: stamp(root.ended_at),
    llmTarget: str(root.llm_target),
    transcript: parseTranscript(result.transcript),
    greeting: parseGreeting(root.greeting),
    recording,
    media: parseMedia(root.media),
    persist: parsePersist(root.persist),
    stats: parseStats(root.summary),
    intentHint: str(result.intent_hint),
    recordingKey: artifact ? str(artifact.blob_key) : (recording?.blobKey ?? ''),
    raw: isRecord(report) ? report : null,
  }
}
